/* Original name @uswitch/trustyle.list */

import * as React from 'react'
import { Themed, useThemeUI } from 'theme-ui'
import { TruThumbUp, TruThumbDown } from '@uswitch/spark-icons/tru'
import { DynamicSparkIcon } from '../spark-icons/dynamic-spark-icon'

const getTitleIcon = (variant, theme) => {
  return variant ? (
    variant === 'pros' ? (
      <TruThumbUp color={theme.elements?.list && theme.elements.list[variant].color} size={24} />
    ) : variant === 'cons' ? (
      <TruThumbDown color={theme.elements?.list && theme.elements.list[variant].color} size={24} />
    ) : null
  ) : null
}

export const ListItem = ({ children, variant, listType = 'bullet', ...props }) => {
  const { theme } = useThemeUI()

  const element = theme?.elements?.list || theme?.list || {} // latter is backwards compatibility
  const before = element?.[listType]?.['::before'] || element?.[listType]?.[':before'] || {}
  const sx = {
    marginBottom: 'sm',
    counterIncrement: 'li',
    position: 'relative',
    paddingLeft: 'md',
    // handle bug in eevee that results in incorrect extra spacing
    p: {
      display: 'inline',
    },
    '::before': {
      // https://www.w3.org/Style/Examples/007/color-bullets.en.html
      position: 'absolute',
      left: '0',
      textAlign: 'right',
      direction: 'rtl',
    },
  }

  if (listType === 'bullet') {
    sx['::before'] = {
      content: '"• "',
      color: element && element.bullet ? element.bullet.color : 'black',
      ...sx['::before'],
      ...before,
    }
  } else if (listType === 'numeric') {
    sx['::before'] = {
      content: 'counter(li)',
      color: element && element.numeric ? element.numeric.color : 'black',
      ...sx['::before'],
      ...before,
    }
  }

  return (
    <li sx={sx} {...props}>
      <If condition={variant}>
        <DynamicSparkIcon
          color={theme.elements?.list[variant]?.color}
          glyph={theme.elements?.list[variant]?.glyph}
          size={24}
        />
      </If>
      {children}
    </li>
  )
}

export const List = ({ children, variant, title, listType = 'bullet', items = [], ...props }) => {
  const { theme } = useThemeUI()

  const getVariant = element =>
    `elements.list.${variant ? `variants.${variant}.${element}` : element}`

  const sx = {
    listStyle: 'none',
    paddingLeft: 'sm',
    marginY: 'sm',
    variant: getVariant('base'),
  }

  const h6 = (
    <Themed.h6 sx={{ variant: getVariant('title') }}>
      <span sx={{ display: 'none' }}>{getTitleIcon(variant, theme)}</span>
      {title}
    </Themed.h6>
  )

  const childrenWithProps = () => {
    return React.Children.map(children, childComponent => {
      return React.cloneElement(childComponent, { listType, variant })
    })
  }

  const listItems = () => {
    return items.map((item, i) => (
      <ListItem key={i} listType={listType} variant={variant}>
        {item}
      </ListItem>
    ))
  }

  const Items = items.length ? listItems() : childrenWithProps()

  return (
    <div>
      {title && h6}
      <Choose>
        <When condition={listType === 'numeric'}>
          <ol {...props} sx={sx}>
            {Items}
          </ol>
        </When>
        <Otherwise>
          <ul {...props} sx={sx}>
            {Items}
          </ul>
        </Otherwise>
      </Choose>
    </div>
  )
}

export default ListItem
